"use client";

import { useEffect, useRef } from "react";
import { createErrorReporter } from "@/lib/observability/report-error";

const report = createErrorReporter(
  "hooks.useCapacitorPlugins",
  "native-plugins",
);

/**
 * Manages Capacitor native plugins: splash screen, status bar, and Android back button.
 *
 * - Hides the splash screen after the WebView has rendered
 * - Sets status bar style based on theme
 * - Handles Android hardware back button (navigates back or exits app)
 *
 * Should be mounted once at the root layout level.
 */
export function useCapacitorPlugins() {
  const backButtonListenerRef = useRef<{ remove: () => void } | null>(null);
  const mediaQueryCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;

    // Detect native platform without importing Capacitor at module level
    import("@capacitor/core").then(({ Capacitor }) => {
      if (cancelled || !Capacitor.isNativePlatform()) return;

      // --- Splash Screen: hide after first paint ---
      import("@capacitor/splash-screen").then(({ SplashScreen }) => {
        if (cancelled) return;
        SplashScreen.hide({ fadeOutDuration: 300 }).catch((e) => {
          console.warn("[splash] hide failed:", e);
          report(e, { action: "hideSplashScreen", severity: "warning" });
        });
      });

      // --- Status Bar: set style based on current theme ---
      import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
        if (cancelled) return;
        const isDark =
          document.documentElement.classList.contains("dark") ||
          window.matchMedia("(prefers-color-scheme: dark)").matches;

        StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch(
          (err) => {
            console.warn(
              "[capacitor-plugins] StatusBar.setStyle() failed:",
              err,
            );
            report(err, { action: "setStatusBarStyle", severity: "warning" });
          },
        );

        // On Android, set the status bar to overlay so content draws behind it
        if (Capacitor.getPlatform() === "android") {
          StatusBar.setOverlaysWebView({ overlay: false }).catch((err) => {
            console.warn("[capacitor-plugins] setOverlaysWebView failed:", err);
            report(err, { action: "setOverlaysWebView", severity: "warning" });
          });
          StatusBar.setBackgroundColor({
            color: isDark ? "#111127" : "#f5f7fc",
          }).catch((err) => {
            console.warn("[capacitor-plugins] setBackgroundColor failed:", err);
            report(err, { action: "setBackgroundColor", severity: "warning" });
          });
        }

        // Listen for theme changes
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleThemeChange = (e: MediaQueryListEvent) => {
          StatusBar.setStyle({
            style: e.matches ? Style.Dark : Style.Light,
          }).catch((err) => {
            console.warn("[capacitor-plugins] theme setStyle failed:", err);
            report(err, { action: "themeChangeSetStyle", severity: "warning" });
          });
          if (Capacitor.getPlatform() === "android") {
            StatusBar.setBackgroundColor({
              color: e.matches ? "#111127" : "#f5f7fc",
            }).catch((err) => {
              console.warn("[capacitor-plugins] theme setBgColor failed:", err);
              report(err, {
                action: "themeChangeSetBgColor",
                severity: "warning",
              });
            });
          }
        };
        mediaQuery.addEventListener("change", handleThemeChange);
        mediaQueryCleanupRef.current = () =>
          mediaQuery.removeEventListener("change", handleThemeChange);
      });

      // --- Android Back Button: navigate back or exit ---
      if (Capacitor.getPlatform() === "android") {
        import("@capacitor/app").then(({ App }) => {
          if (cancelled) return;
          App.addListener("backButton", ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              // On the root page, minimize the app instead of exiting
              App.minimizeApp();
            }
          }).then((handle) => {
            if (cancelled) {
              handle.remove();
            } else {
              backButtonListenerRef.current = handle;
            }
          });
        });
      }
    });

    return () => {
      cancelled = true;
      backButtonListenerRef.current?.remove();
      backButtonListenerRef.current = null;
      mediaQueryCleanupRef.current?.();
      mediaQueryCleanupRef.current = null;
    };
  }, []);
}
